import {createApi, retry} from '@reduxjs/toolkit/query/react';
import axios from 'axios';
import store from '../store';
import {getSharedDocumentHeader} from './apiUtils';
import {selectEffectiveOrg} from './session';
import {isRetryableError} from '../util/apiErrors';

const axiosBaseQuery =
  ({baseUrl} = {baseUrl: ''}) =>
  async ({url, method = 'GET', data, params, headers}) => {
    const state = store.getState();
    const {session} = state;
    const effectiveOrg = selectEffectiveOrg(state);

    const _headers = {
      'x-inscope-org': effectiveOrg?.orgId ?? session.currentOrg?.orgId,
      ...getSharedDocumentHeader(),
      ...headers,
    };

    if (session.accessToken) {
      _headers.Authorization = `Bearer ${session.accessToken}`;
    }

    try {
      const response = await axios({
        url: `${baseUrl}${url}`,
        method,
        data,
        params,
        headers: _headers,
      });
      return {data: response.data};
    } catch (axiosError) {
      const error = {
        code: axiosError.code,
        status: axiosError.response?.status,
        data: axiosError.response?.data || axiosError.message,
      };
      if (!isRetryableError(error)) {
        retry.fail(error);
      }
      return {error};
    }
  };

export const pdfReviewApiSlice = createApi({
  reducerPath: 'pdfRevewApi',
  baseQuery: retry(
    axiosBaseQuery({baseUrl: process.env.REACT_APP_COPILOT_V2_API_URL}),
    {
      maxRetries: 3,
    }
  ),
  tagTypes: ['PDFFootings'],
  endpoints: (builder) => ({
    parsePDF: builder.mutation({
      query: ({documentId}) => ({
        url: `/pdf/parse/${documentId}`,
        method: 'POST',
      }),
    }),

    runPDFFootings: builder.mutation({
      query: ({documentId}) => ({
        url: `/suggestions/pdf-footings/${documentId}`,
        method: 'POST',
      }),
    }),

    runPDFInconsistencies: builder.mutation({
      query: ({documentId}) => ({
        url: `/suggestions/pdf-inconsistencies/${documentId}`,
        method: 'POST',
      }),
    }),

    getJobStatus: builder.query({
      query: ({jobId, checkType}) => ({
        url: `/suggestions/${jobId}/${checkType}`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useParsePDFMutation,
  useRunPDFFootingsMutation,
  useRunPDFInconsistenciesMutation,
  useLazyGetJobStatusQuery,
} = pdfReviewApiSlice;
